import React, { Component } from 'react';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withNamespaces } from 'react-i18next';
import { Row } from 'reactstrap';
import Table from 'components/tables';
import TableHead from 'components/tables/tableHead';
import TableBody from 'components/tables/tableBody';
import TableCell from 'components/tables/tableCell';
import TableRow from 'components/tables/tableRow';
import { SubmissionError } from 'redux-form';
import LoadingIcon from 'mdi-react/LoadingIcon';
import NotificationSystem from 'rc-notification';

import Panel from 'components/Panel';
import { NOTIFICATION_NODE_STYLE } from 'constants/misc';
import BasicNotification from 'components/Notification';
import { updateAccount } from 'redux/actions/accountActions';
import { getAccountEmailConfigs, updateEmailConfig } from 'redux/actions/emailConfigActions';
import { getIndustries } from 'redux/actions/industryActions';
import queryString from 'query-string';
import DetailsForm from './DetailsForm';
import EmailSettingsForm from './EmailSettingsForm';
import LeadsHandlingForm from './LeadsHandlingForm';
import PermissioningForm from './PermissioningForm';
import EmailConfigCreatePrompt from './EmailConfigForms/EmailConfigCreatePrompt';
import EmailConfigRow from './EmailConfigRow';

const UPDATE_FORM_SUCCESS_MSG = 'Form successfully updated';

class DetailsTab extends Component {
  static propTypes = {
    account: PropTypes.shape({
      id: PropTypes.number,
      organization: PropTypes.number,
      name: PropTypes.string,
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
    }).isRequired,
    getIndustries: PropTypes.func.isRequired,
    getAccountEmailConfigs: PropTypes.func.isRequired,
    updateEmailConfig: PropTypes.func.isRequired,
    updateAccount: PropTypes.func.isRequired,
    match: PropTypes.shape({
      params: PropTypes.shape({
        id: PropTypes.string,
      }),
      path: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }).isRequired,
    industries: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      options: PropTypes.array,
    }).isRequired,
    accountEmailConfigs: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      results: PropTypes.array,
    }).isRequired,
    accountEmailConfig: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
    }).isRequired,
    config: PropTypes.shape({
      emailBackend: PropTypes.string.isRequired,
    }).isRequired,
  };

  constructor(props) {
    super(props);
    this.changeIsPrimary = this.changeIsPrimary.bind(this);
  }

  componentDidMount() {
    this.props.getIndustries();
    this.props.getAccountEmailConfigs(this.props.match.params.id);
    // eslint-disable-next-line no-return-assign
    NotificationSystem.newInstance({}, n => this.notification = n);
    setTimeout(() => {
      const parsedParams = queryString.parse(window.location.search);
      if (parsedParams.status === 'success') {
        this.showNotification(parsedParams.message);
      } else if (parsedParams.status === 'fail') {
        this.showNotification(parsedParams.message, 'danger', 25);
      }
    }, 1000);
  }

  componentWillUnmount() {
    this.notification.destroy();
  }

  onEmailCreateSuccess = (email) => {
    this.showNotification(`Email(${email}) created.`);
  };

  showNotification = (msg, status = 'success', duration = 3) => {
    this.notification.notice({
      content: <BasicNotification
        color={status}
        message={msg}
      />,
      duration,
      closable: false,
      style: NOTIFICATION_NODE_STYLE,
      className: 'alert--colored right-up',
    });
  }

  async handleDetailsForm(formData) {
    try {
      const industries = formData.selectedIndustryTags;
      await this.props.updateAccount(
        this.props.match.params.id,
        {
          email: formData.email,
          products: formData.selectedProducts ? formData.selectedProducts.map(product => ({ name: product })) : [],
          industries: industries && industries.map(l => (l.value)),
        },
        true, // disabling loader for seamless update
      );
      this.showNotification(UPDATE_FORM_SUCCESS_MSG);
    } catch (error) {
      if (error.errors) {
        throw new SubmissionError(error.errors);
      }
    }
  }

  async handlePermissioningForm(formData) {
    try {
      await this.props.updateAccount(
        this.props.match.params.id,
        {
          client_manages_replies: formData.client_manages_replies,
          inbox_visible: formData.inbox_visible,
        },
        true, // disabling loader for seamless update
      );
      this.showNotification(UPDATE_FORM_SUCCESS_MSG);
    } catch (error) {
      if (error.errors) {
        throw new SubmissionError(error.errors);
      }
    }
  }

  async handleEmailSettingsForm(formData) {
    try {
      await this.props.updateAccount(
        this.props.match.params.id,
        {
          email_delay: formData.delayBeforeEmail,
          max_emails_to_a_contact_per_day: formData.maxEmails,
        },
        true, // disabling loader for seamless update
      );
      this.showNotification(UPDATE_FORM_SUCCESS_MSG);
    } catch (error) {
      if (error.errors) {
        throw new SubmissionError(error.errors);
      }
    }
  }

  async handleLeadsHandlingForm(formData) {
    try {
      await this.props.updateAccount(
        this.props.match.params.id,
        {
          accepts_sponsorships: formData.acceptsSponsorships,
          response_email_forwards: formData.forwardResponseEmailsTo.join(','),
          forward_email_template: formData.forwardEmailTemplate.value,
        },
        true, // disabling loader for seamless update
      );
      this.showNotification(UPDATE_FORM_SUCCESS_MSG);
    } catch (error) {
      if (error.errors) {
        throw new SubmissionError(error.errors);
      }
    }
  }

  async changeIsPrimary(emailConfigId, isPrimary) {
    await this.props.updateEmailConfig(
      this.props.account.organization,
      emailConfigId,
      { accounts: [this.props.account.id], is_primary: !isPrimary },
    );
    await this.props.getAccountEmailConfigs(this.props.account.id);
  }

  render() {
    const { account, industries, accountEmailConfigs } = this.props;
    const isIndustryLoading = industries.loading;
    const isAccountLoading = account.loading;
    const isAccountEmailConfigsLoading = accountEmailConfigs.loading;
    const totalDefaultEmailConfigs = accountEmailConfigs.results.filter(config => config.is_primary).length;

    if (isIndustryLoading || isAccountLoading || isAccountEmailConfigsLoading) {
      return (
        <div className="loader"><LoadingIcon /></div>
      );
    }

    return (
      <React.Fragment>

        <Row>
          <Panel title="General">
            <DetailsForm onSubmit={data => this.handleDetailsForm(data)} />
          </Panel>
        </Row>

        <Row>
          <Panel title="Sending email accounts">

            {(!accountEmailConfigs.loading && !accountEmailConfigs.results.length >= 1) ? (
              <h5 className="text-center mt-5">No email accounts setup yet</h5>
            ) : (
              <Table className="volt-table">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Email</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>Daily limit</TableCell>
                    <TableCell>Delay</TableCell>
                    <TableCell>Added on</TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!this.props.accountEmailConfigs.loading &&
                    this.props.accountEmailConfigs.results.map(emailConf => (
                      <EmailConfigRow
                        key={emailConf.id}
                        emailConfig={emailConf}
                        changeIsPrimary={this.changeIsPrimary}
                        showNotification={this.showNotification}
                        totalEmailConfigs={accountEmailConfigs.results.length}
                        totalDefaultEmailConfigs={totalDefaultEmailConfigs}
                      />
                    ))
                  }
                </TableBody>
              </Table>
            )}
            {this.props.account.loaded &&
              <EmailConfigCreatePrompt
                onEmailCreateSuccess={e => this.onEmailCreateSuccess(e)}
              />
            }
          </Panel>
        </Row>

        <Row>
          <Panel title="Email sending settings">
            <EmailSettingsForm onSubmit={data => this.handleEmailSettingsForm(data)} />
          </Panel>
        </Row>

        <Row>
          <Panel title="Leads handling">
            <LeadsHandlingForm onSubmit={data => this.handleLeadsHandlingForm(data)} />
          </Panel>
        </Row>

        <Row>
          <Panel title="Client login permissions">
            <PermissioningForm onSubmit={data => this.handlePermissioningForm(data)} />
          </Panel>
        </Row>
      </React.Fragment>
    );
  }
}

export default compose(
  withNamespaces('common'),
  connect(
    state => ({
      industries: state.industries,
      account: state.account,
      accountEmailConfigs: state.accountEmailConfigs,
      accountEmailConfig: state.accountEmailConfig,
      config: state.config,
    }),
    {
      getIndustries,
      updateAccount,
      getAccountEmailConfigs,
      updateEmailConfig,
    },
  ),
)(DetailsTab);
